import React from 'react';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import { display, eyebrow } from '../actionables/brand';

// Shared page header for the new design language: optional eyebrow, a display-font title with an
// optional leading icon, an optional subtitle, and an optional right-aligned actions slot. The
// reduced-motion-safe entrance is baked in because both consuming pages wrapped the header in the
// identical motion.div. Reproduced byte-for-byte from the former inline headers.
//
// When `actions` is provided the header renders the space-between flex row (Inventory shape); when
// absent it renders the plain stacked title block (Draft Bundles shape). Icon colour/size is set by
// the caller (passed as a node); subtitle sits in text.secondary.
const PageHeader: React.FC<{
    eyebrow?: string;
    title: string;
    icon?: React.ReactNode;
    subtitle?: React.ReactNode;
    actions?: React.ReactNode;
    titleSize?: string | Record<string, string>;
}> = ({ eyebrow: eyebrowText, title, icon, subtitle, actions, titleSize = { xs: '1.9rem', sm: '2.3rem' } }) => {
    const reduceMotion = useReducedMotion();

    // Bake the standard icon treatment (color #DC5829, 1.9rem) as a DEFAULT so call sites may pass a
    // bare icon. The default `sx` is spread FIRST and the element's own `sx` AFTER, so MUI's
    // left-to-right array merge lets an explicit caller `sx` win (no doubling). Call sites that pass
    // the same values explicitly render byte-identically.
    const renderedIcon = React.isValidElement(icon)
        ? React.cloneElement(icon as React.ReactElement<{ sx?: SxProps<Theme> }>, {
              sx: [
                  { color: '#DC5829', fontSize: '1.9rem' },
                  ...(() => {
                      const iconSx = (icon as React.ReactElement<{ sx?: SxProps<Theme> }>).props.sx;
                      return Array.isArray(iconSx) ? iconSx : iconSx ? [iconSx] : [];
                  })(),
              ],
          })
        : icon;

    const titleBlock = (
        <Box>
            {eyebrowText && (
                <Typography sx={{ ...eyebrow, color: '#8A8078' }}>{eyebrowText}</Typography>
            )}
            <Typography sx={{ ...display(titleSize), mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                {renderedIcon}
                {title}
            </Typography>
            {subtitle && (
                <Typography variant="body1" color="text.secondary" sx={{ mt: 0.75 }}>
                    {subtitle}
                </Typography>
            )}
        </Box>
    );

    return (
        <Box
            component={motion.div}
            initial={reduceMotion ? false : { opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            sx={{ mb: 2.5 }}
        >
            {actions ? (
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 } }}>
                    {titleBlock}
                    {/* Actions render on all breakpoints: on xs they wrap into a full-width row below
                        the title; on sm+ they stay right-aligned next to the title (desktop unchanged). */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, width: { xs: '100%', sm: 'auto' } }}>
                        {actions}
                    </Box>
                </Box>
            ) : (
                titleBlock
            )}
        </Box>
    );
};

export default PageHeader;
